'use client';
import { useMutation, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { ElowenApiError, elowenClient } from "../elowenClient";
import { SaveConflictError } from '../saveFeedback';
import { authGeneration } from '../token';
import type { BrainConversationArchiveRequest, BrainConversationArchiveResponse } from '../types';
import { QUERY_KEYS } from "../queries";
import type { BrainProvider, ConfigPatch, ConfigSnapshot } from "../types";
import { adoptConfigConflict } from "./system";

/** Refresh all projections of a conversation after an accepted write, even if its surface unmounts. */
export async function invalidateConversationLists(qc: QueryClient): Promise<void> {
  await Promise.all([
    qc.invalidateQueries({ queryKey: ['brain-sessions'] }),
    qc.invalidateQueries({ queryKey: ['brain-managed-sessions'] }),
    qc.invalidateQueries({ queryKey: QUERY_KEYS.brainConversationLinks }),
    qc.invalidateQueries({ queryKey: ['brain-search'] }),
  ]);
}

/** The response is authoritative; uncertain writes are never resent, and stale account completions touch no cache. */
export async function setConversationArchive(qc: QueryClient, sessionId: string, request: BrainConversationArchiveRequest): Promise<BrainConversationArchiveResponse> {
  const generation = authGeneration();
  const assertCurrent = () => { if (authGeneration() !== generation) throw new Error('Account changed during archive'); };
  const reload = async () => {
    assertCurrent();
    await qc.cancelQueries({ queryKey: ['brain-sessions'], exact: true });
    assertCurrent();
    await qc.fetchQuery({ queryKey: ['brain-sessions'], queryFn: () => elowenClient.brainSessions('all'), staleTime: 0 });
    assertCurrent();
    await qc.refetchQueries({ queryKey: ['brain-managed-sessions'], exact: true }, { throwOnError: true });
    assertCurrent();
  };
  let response: BrainConversationArchiveResponse;
  try {
    response = await elowenClient.brainSetConversationArchive(sessionId, request);
  } catch (error) {
    assertCurrent();
    if (error instanceof ElowenApiError && error.code === 'archive_conflict') throw new SaveConflictError({ reload });
    throw error;
  }
  assertCurrent();
  await invalidateConversationLists(qc);
  assertCurrent();
  return response;
}

export async function renameConversation(qc: QueryClient, id: string, title: string): Promise<void> {
  await elowenClient.brainRenameSession(id, title);
  await invalidateConversationLists(qc);
}

export async function setConversationPinned(qc: QueryClient, id: string, pinned: boolean): Promise<void> {
  await elowenClient.brainSetSessionPinned(id, pinned);
  await invalidateConversationLists(qc);
}

/** Force the shared catalog worker and refresh both admin and regular model pickers after it settles. */
export function useRefreshModelCatalog() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => elowenClient.refreshModelCatalog(),
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['brain-models'] }),
        qc.invalidateQueries({ queryKey: ['grantable-brain-models'] }),
      ]);
    },
  });
}

type SavedProviders = NonNullable<NonNullable<ConfigPatch['brain']>['providers']>;

/** Replace the brain provider list (Settings → Brain). Refreshes the config and the models dropdown.
 *
 *  The variable is an EDIT of the list rather than the list itself: autosaving controls fire saves back to
 *  back, so each one is applied to the cached list and revision as they stand when it runs. The shared
 *  scope runs them one after another, each after the previous answer wrote its snapshot into the cache, so
 *  a second save never sends a stale revision or undoes the first one's change. A 409 adopts the daemon's
 *  current snapshot, so the next save starts from it. */
export function useSaveBrainProviders() {
  const qc = useQueryClient();
  return useMutation({
    scope: { id: 'brain-providers' },
    mutationFn: (edit: (current: readonly BrainProvider[]) => SavedProviders) => {
      const current = qc.getQueryData<ConfigSnapshot>(QUERY_KEYS.config);
      if (!current) throw new Error('The configuration is not loaded.');
      return elowenClient.updateConfig({ brain: { providers: edit(current.brain?.providers ?? []) } }, current.revision);
    },
    onSuccess: async (snapshot) => {
      qc.setQueryData(QUERY_KEYS.config, snapshot);
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['brain-models'] }),
        qc.invalidateQueries({ queryKey: ['grantable-brain-models'] }),
      ]);
    },
    onError: (error) => adoptConfigConflict(qc, error),
  });
}

/** Drop ONE account's stored login. The account entry and its settings stay in the config: disconnecting
 *  is not deleting, and the operator can sign the same account back in. */
export function useBrainOauthDisconnect() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (account: { providerId: string; connectionId: string }) => elowenClient.brainOauthDisconnect(account.providerId, account.connectionId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['brain-oauth'] }),
  });
}

/** Membership changes. The `['brain-sessions']` prefix holds the list rows (faces, capabilities) and each
 *  conversation's members and invitable accounts, so one invalidation refreshes all three. A refusal
 *  rejects with the daemon's `ElowenApiError` (status and typed `code`) for the caller to map. */
export function useInviteParticipant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { sessionId: string; userId: number }) => elowenClient.brainInviteParticipant(v.sessionId, v.userId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['brain-sessions'] }),
  });
}

export function useRemoveParticipant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { sessionId: string; userId: number }) => elowenClient.brainRemoveParticipant(v.sessionId, v.userId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['brain-sessions'] }),
  });
}

/** The owner pauses or resumes a shared conversation's agent. The list rows carry `agentPaused`, so the
 *  same invalidation refreshes the bound conversation's membership; every other member follows through
 *  the daemon's `membership-changed` resync. */
export function useSetAgentPaused() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { sessionId: string; paused: boolean }) => elowenClient.brainSetAgentPaused(v.sessionId, v.paused),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['brain-sessions'] }),
  });
}
